import * as React from 'react';

import { CdnImagePath } from 'lib/cdn-image-path';
import Image from 'lib/image-container';
import { findDefaultLexeme, findLexeme } from 'lib/mock-lexemes';
import { throttle } from 'lib/throttle';
import useMediaQuery, { MediaQueryWidth } from 'lib/use-media-query';

import Icon from 'components/icon/icon';
import PageSectionModals from 'components/page/sections/page-section-modals';

type GlobalDatingTrendsInfographicContainerProps = {
  lang?: string;
  title?: string;
  lexemes?: string[];
};

type frontContentProps = {
  isMobile?: boolean;
  backgroundImageSrcLandscape: string;
  backgroundImageSrcPortrait: string;
  alt: string;
};
const frontContent = ({
  isMobile,
  backgroundImageSrcLandscape,
  backgroundImageSrcPortrait,
  alt,
}: frontContentProps) => (
  <div
    style={{
      position: 'absolute',
      top: 0,
      overflow: 'hidden',
      height: '100%',
      width: 'calc(100% + var(--content-section-padding-horizontal))',
      margin: '0 calc(-1 * var(--content-section-padding-horizontal))',
      display: 'flex',
      pointerEvents: 'none',
    }}
  >
    <div
      className="page__section-background-text-image"
      style={{
        position: 'relative',
        overflow: 'hidden',
        maxHeight: 'calc(100vh + var(--header-min-height))',
        minHeight: 'var(--min-height)',
        marginTop: 'var(--margin-top)',
        flex: '1 1 auto',
      }}
    >
      <Image
        priority={true}
        src={!isMobile ? backgroundImageSrcLandscape : backgroundImageSrcPortrait}
        alt={alt}
        style={{
          objectPosition: 'bottom center',
          objectFit: 'cover',
        }}
      />
    </div>
  </div>
);

export const GlobalDatingTrendsInfographicContainer: React.FC<
  GlobalDatingTrendsInfographicContainerProps
> = (propsServer) => {
  const lexeme = (id: string) =>
    propsServer.lexemes ? findLexeme(propsServer.lexemes, id) : findDefaultLexeme(id);

  const props = {
    backgroundImageSrcPortrait: `${CdnImagePath}/landing/assets/photos/global-dating-trends-infographic-portrait.jpg`,
    backgroundImageSrcLandscape: `${CdnImagePath}/landing/assets/photos/global-dating-trends-infographic-landscape.jpg`,
    title: propsServer.title || 'Bumble’s 2025 Global dating Trends',
    items: [
      {
        iconName: Icon.Name.GlobalTrendsMicroMance,
        title: lexeme('bumblesite@global_trends_report_2025.block.one.title'),
        text: lexeme('bumblesite@global_trends_report_2025.block.one.text'),
        backgroundColor: '#FD763C',
      },
      {
        iconName: Icon.Name.GlobalTrendsDWM,
        title: lexeme('bumblesite@global_trends_report_2025.block.two.title'),
        text: lexeme('bumblesite@global_trends_report_2025.block.two.text'),
        backgroundColor: '#FBCA00',
      },
      {
        iconName: Icon.Name.GlobalTrendsOnTheSamePage,
        title: lexeme('bumblesite@global_trends_report_2025.block.three.title'),
        text: lexeme('bumblesite@global_trends_report_2025.block.three.text'),
        backgroundColor: '#D091E7',
      },
      {
        iconName: Icon.Name.GlobalTrendsMaleCasting,
        title: lexeme('bumblesite@global_trends_report_2025.block.four.title'),
        text: lexeme('bumblesite@global_trends_report_2025.block.four.text'),
        backgroundColor: '#89A3FF',
      },
      {
        iconName: Icon.Name.GlobalTrendsFutureProofing,
        title: lexeme('bumblesite@global_trends_report_2025.block.five.title'),
        text: lexeme('bumblesite@global_trends_report_2025.block.five.text'),
        backgroundColor: '#1CB96D',
      },
      {
        iconName: Icon.Name.GlobalTrendsGuysThatGetIt,
        title: lexeme('bumblesite@global_trends_report_2025.block.six.title'),
        text: lexeme('bumblesite@global_trends_report_2025.block.six.text'),
        backgroundColor: '#C4C8D5',
      },
    ],
    alt: lexeme('bumblesite@global_trends_report_2025.block.disclaimer') || '',
  };
  const isMobile = useMediaQuery(MediaQueryWidth['tablet-sm']);
  const divRef = React.useRef<HTMLDivElement>(null);

  const measureHeight = () => {
    if (divRef.current) {
      document.body.style.setProperty(
        '--info-graphic-bg-height',
        `${divRef.current.clientHeight}px`,
      );
    }
  };

  React.useEffect(() => {
    measureHeight();

    const throttledUpdate = throttle(measureHeight, 300);
    global.window.addEventListener('resize', throttledUpdate);

    return () => {
      global.window.removeEventListener('resize', throttledUpdate);
      document.body.style.removeProperty('--info-graphic-bg-height');
    };
  }, []);

  return (
    <PageSectionModals
      lang={propsServer.lang}
      title={props.title}
      cards={props.items}
      align="center"
      innerRef={divRef}
      disclaimer={props.alt}
      frontContent={frontContent({
        isMobile,
        backgroundImageSrcLandscape: props.backgroundImageSrcLandscape,
        backgroundImageSrcPortrait: props.backgroundImageSrcPortrait,
        alt: props.alt,
      })}
    />
  );
};
